import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { publicAxios, baseUrl } from 'Api';
import logo from 'Assets/Login Logo.png';
import SharedVideoPlayer from './SharedVideoPlayer';
import SharedAlbumPlayer from './SharedAlbumPlayer';
import SharedCategoryPlayer from './SharedCategoryPlayer';
import AddToProjectorModal from './AddToProjectorModal';
import styles from './index.module.css';

// ChottuLink smart link used for the "Open in Projector App" hand-off. Building
// it as a plain URL (no API key needed) lets ChottuLink own all the routing:
//   • app installed  → opens the app straight on the shared-content screen
//   • not installed  → App Store (via the App Store ID set in the ChottuLink
//                      dashboard), then the ChottuLink SDK hands the params to
//                      the app on first launch (deferred deep linking)
//   • desktop/no-app → the link's configured web fallback
const CHOTTULINK_DOMAIN = 'projectorapp.chottu.link';
const APP_DEEPLINK_PATH = 'sharecontent'; // → https://projectorapp.chottu.link/sharecontent?key=...

// Desktop can't open the app, so the arrival popup is mobile-only — same UA
// test as the /contribute and /contribute-mobile-upload arrival popups.
const isMobileDevice = () => /iphone|ipad|ipod|android/i.test(navigator.userAgent || '');

// Public, no-login landing page for a shared video or album (/share/:key).
export const SharedItem = () => {
  const { key } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  // Evaluated on first render so the popup paints with the page, not a frame late.
  const [showAppPopup, setShowAppPopup] = useState(isMobileDevice);

  useEffect(() => {
    let active = true;
    const fetchShared = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await publicAxios.post(
          `${baseUrl}/getSharedItem`,
          { key, page: 1, limit: 12 },
          { headers: { 'Content-Type': 'application/json' } }
        );
        if (!active) return;
        if (response.data?.success && response.data?.data) {
          setData(response.data.data);
        } else {
          setError(response.data?.message || 'This link is no longer available.');
          // Dead key — nothing for the app to open, so drop the hand-off popup.
          setShowAppPopup(false);
        }
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message || 'This link is no longer available.');
        setShowAppPopup(false);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchShared();
    return () => { active = false; };
  }, [key]);

  const owner = data?.owner;
  const item = data?.item;
  const isVideo = data?.type === 'video';
  const isCategory = data?.type === 'category';
  const itemTitle = item?.title || item?.name || item?.video_title || item?.album_title;

  // Human summary of what's being added, e.g.
  //   "Sunset Trip album with 24 photos"
  //   "18 videos from Nature"
  //   "Morning Yoga video"
  const buildSummary = () => {
    if (!item) return '';
    const title = itemTitle || 'this item';
    if (data?.type === 'album') {
      const photoCount = Array.isArray(item.photos)
        ? item.photos.length
        : (Number(item.photo_count) || 0);
      return `${title} album with ${photoCount} ${photoCount === 1 ? 'photo' : 'photos'}`;
    }
    if (data?.type === 'category') {
      const videoCount = item.video_count ?? (Array.isArray(item.videos) ? item.videos.length : 0);
      return `${videoCount} ${videoCount === 1 ? 'video' : 'videos'} from ${title}`;
    }
    return `${title} video`;
  };

  // Hand off to the native app via a ChottuLink smart link. ChottuLink owns all
  // the routing (open app / App Store + deferred deep link / web fallback), so
  // there is no raw projector:// URL or store-fallback timer built by hand.
  // Unlike the other flows, the share key lives in the path (/share/:key) rather
  // than the query string, so it has to be added explicitly; anything already in
  // the query string is forwarded as-is. shareType mirrors mobile-upload's
  // uploadType flag — the app gets told which screen to open instead of having
  // to infer it. It is only known once getSharedItem resolves, so an early tap
  // hands off with just the key and the app resolves the type itself.
  const handleOpenApp = () => {
    const params = new URLSearchParams(window.location.search);
    params.set('key', key);
    if (data?.type) params.set('shareType', data.type);
    const query = params.toString();
    window.location.href = `https://${CHOTTULINK_DOMAIN}/${APP_DEEPLINK_PATH}${query ? `?${query}` : ''}`;
  };

  const renderOwner = () => {
    if (!owner) return null;
    const initial = (owner.name?.[0] || '?').toUpperCase();
    return (
      <div className={styles.owner}>
        <div className={styles.ownerAvatar}>
          {owner.image ? (
            <img src={owner.image} alt={owner.name} />
          ) : (
            <span>{initial}</span>
          )}
        </div>
        <div className={styles.ownerText}>
          <span className={styles.ownerLabel}>Shared by</span>
          <span className={styles.ownerName}>{owner.name || 'A Projector user'}</span>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <a href="https://www.projectorstream.com/" className={styles.logoLink}>
          <img src={logo} alt="Projector" className={styles.logo} />
        </a>
        <div className={styles.topRight}>
          <button
            type="button"
            className={styles.addButton}
            onClick={() => setAddOpen(true)}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
            <span>Add to your Projector</span>
          </button>
          {!loading && !error && renderOwner()}
        </div>
      </div>

      <div className={styles.body}>
        {loading && (
          <div className={styles.centerState}>
            <div className={styles.spinner} />
            <p>Loading…</p>
          </div>
        )}

        {!loading && error && (
          <div className={styles.centerState}>
            <div className={styles.errorIcon}>🔗</div>
            <h2 className={styles.errorTitle}>{error}</h2>
            <p className={styles.errorSubtitle}>
              The owner may have stopped sharing this content, or the link is incorrect.
            </p>
            <button
              type="button"
              className={styles.homeButton}
              onClick={() => { window.location.href = 'https://www.projectorstream.com/'; }}
            >
              Go to Projector
            </button>
          </div>
        )}

        {!loading && !error && item && (
          isCategory ? (
            <SharedCategoryPlayer item={item} shareKey={key} ownerName={owner?.name} />
          ) : (
            <div className={isVideo ? styles.stage : styles.albumStage}>
              {isVideo ? (
                <SharedVideoPlayer item={item} onBack={() => navigate(-1)} />
              ) : (
                <SharedAlbumPlayer item={item} />
              )}
            </div>
          )
        )}
      </div>

      {/* ── "Open in Projector App" arrival popup (mobile only) ─────────────── */}
      {showAppPopup && (
        <div className={styles.openAppOverlay}>
          <div className={styles.openAppCard}>
            <div className={styles.openAppIcon}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className={styles.openAppTitle}>Open in Projector App</h2>
            <p className={styles.openAppDescription}>
              For the best experience, open this shared content in the Projector app.
            </p>
            <button
              type="button"
              className={styles.openAppButton}
              onClick={handleOpenApp}
            >
              Open App
            </button>
            <button
              type="button"
              className={styles.openAppDismiss}
              onClick={() => setShowAppPopup(false)}
            >
              Continue in Browser
            </button>
          </div>
        </div>
      )}

      <AddToProjectorModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onView={() => navigate('/dashboard')}
        itemTitle={itemTitle}
        itemType={data?.type}
        summary={buildSummary()}
        shareKey={key}
        ownerName={owner?.name}
      />
    </div>
  );
};

export default SharedItem;
